import { ErrorMessage } from "@components/atoms/ErrorMessage"
import { MobileAccordion } from "@components/controls/Accordion"
import { Button } from "@components/controls/Button"
import { fieldTypes } from "@components/forms/lib/form-support"
import { ZodForm } from "@components/forms/lib/ZodForm"
import { LoadingResults } from "@components/layout/LoadingResults"
import { confirmStripeIntentRedirect } from "@components/stripe/confirmIntentRedirect"
import { GetPaymentMethodFunction, StripePaymentContext } from "@components/stripe/StripePaymentField"
import { IconCaretLeft } from "@components/svg/Icons"
import { useCartIntent, useCheckout, useNewsletterSubscribe } from "@hooks/queries"
import { getErrorMessage } from "@utils/errors"
import { useMemo, useState } from "react"
import { z } from "zod"
import { MetaDataInput } from "../../../types.graphql"
import { CartController } from "../hooks/useCartController"
import { CartControllerSummary } from "./CartSummary"
import { useMyInfoForm } from "./MyInfoForm"

type Props = {
  controller: CartController
  agreements: {
    membership?: string
    terms?: string
    newsletter?: string
  }
  onGoBack?: () => void
  onSuccess: (order: { url: string; id: number }) => void
  checkoutMetadata?: MetaDataInput[]
}

export function CheckoutForm(props: Props) {
  const myInfoForm = useMyInfoForm({ mode: "billing", requireAddress: true, prefill: true })

  const requiresPayment = useMemo(() => {
    return typeof props.controller.data?.total === "string" ? parseFloat(props.controller.data?.total.replace(/[^0-9\.]/g, "")) > 0 : true
  }, [props.controller.data?.total])

  const schema = useMemo(() => {
    const requiredTerm = z.boolean().refine((v) => v, { message: "You must agree to these terms to continue" })
    const optionalTerm = z.boolean().optional()
    return z.object({
      info: myInfoForm.schema,
      paymentMethod: fieldTypes.paymentMethod,
      agreeMembership: props.agreements.membership ? requiredTerm : optionalTerm,
      agreeNewsletter: optionalTerm,
      agreeTerms: props.agreements.terms ? requiredTerm : optionalTerm,
    })
  }, [myInfoForm.schema])

  // const [paymentIntent, setPaymentIntent] = useState<null | {
  //   intentId: string
  //   clientSecret: string
  //   customer: string
  // }>(null)
  // useEffect(() => {
  //   setPaymentIntent(null)
  //   if (!requiresPayment) return
  //   useCartIntent.fetch(undefined).then((data) => {
  //     setPaymentIntent({
  //       intentId: data.cart?.stripePaymentIntent?.id!,
  //       clientSecret: data.cart?.stripePaymentIntent?.clientSecret!,
  //       customer: data.cart?.stripePaymentIntent?.customer!,
  //     })
  //   })
  // }, [props.controller.data, requiresPayment])

  const [getPaymentMethod, setGetPaymentMethod] = useState<null | { getPaymentMethod: GetPaymentMethodFunction }>(null)

  const checkout = useCheckout()

  const [checkoutError, setCheckoutError] = useState<null | string>(null)

  if (myInfoForm.loading) return <LoadingResults />

  return (
    <div className="grid-auto">
      <div className="col-span-12 sm:col-span-4 sm:order-3 lg:col-span-2 pb-4">
        <MobileAccordion header="Order Summary">
          <CartControllerSummary controller={props.controller} />
        </MobileAccordion>
      </div>
      <div className="col-span-12 sm:col-span-5 lg:col-span-4">
        <ErrorMessage className="mb-4" scroll>
          {checkoutError ? checkoutError : checkout.isError ? getErrorMessage(checkout.error.message) : null}
        </ErrorMessage>
        <StripePaymentContext
          onCanGetPaymenthMethod={(e) => {
            setGetPaymentMethod({ getPaymentMethod: e.getPaymentMethod })
          }}
          clientSecret={null}
          publishableKey={props.controller.data?.stripePublishableKey!}
        >
          <ZodForm
            schema={schema}
            onSubmit={async (data) => {
              setCheckoutError(null)
              const method = await getPaymentMethod?.getPaymentMethod({
                address: {
                  line1: data.info.address.address1,
                  line2: data.info.address.address2,
                  city: data.info.address.city,
                  country: data.info.address.country,
                  postal_code: data.info.address.postcode,
                  state: data.info.address.state,
                },
                email: data.info.email,
                name: data.info.name.first + " " + data.info.name.last,
                phone: data.info.phone,
              })
              // console.log("Result", method)
              if (method?.error) {
                setCheckoutError(method.error)
                return
              }
              const metaData: MetaDataInput[] = []
              if (method?.paymentMethodId) {
                metaData.push({
                  key: "_stripe_source_id",
                  value: method.paymentMethodId,
                })
                // if (paymentIntent?.customer) {
                //   metaData.push({
                //     key: "_stripe_customer_id",
                //     value: paymentIntent?.customer!,
                //   })
                // }
                // if (paymentIntent?.intentId) {
                //   metaData.push({
                //     key: "_stripe_intent_id",
                //     value: paymentIntent?.clientSecret!,
                //   })
                // }
                metaData.push({
                  key: "_stripe_is_deferred_intent",
                  value: "true",
                })
                metaData.push({
                  key: "_stripe_new_payment_method",
                  value: "true",
                })
              }
              if (props.checkoutMetadata) {
                metaData.push(...props.checkoutMetadata)
              }
              let result
              try {
                result = await checkout.mutateAsync({
                  billing: {
                    email: data.info.email,
                    firstName: data.info.name.first,
                    lastName: data.info.name.last,
                    phone: data.info.phone,
                    address1: data.info.address.address1,
                    address2: data.info.address.address2,
                    city: data.info.address.city,
                    country: data.info.address.country as any,
                    postcode: data.info.address.postcode,
                    state: data.info.address.state,
                  },
                  metaData: metaData,
                })
              } catch (e) {
                // The error is displayed via checkout.isError above.
                return
              }

              if (result.checkout?.result !== "success") {
                setCheckoutError("Your card was declined, or there was an error processing your payment. Please try again.")
                return
              }

              const redirect = result.checkout?.redirect || ""

              // The gateway may require client-side confirmation (e.g. 3D Secure),
              // indicated by a #wc-stripe-confirm redirect hash.
              const confirmation = await confirmStripeIntentRedirect({
                redirect,
                publishableKey: props.controller.data?.stripePublishableKey!,
                paymentMethodId: method?.paymentMethodId,
              })
              if (confirmation.handled && confirmation.error) {
                setCheckoutError(confirmation.error)
                return
              }

              if (data.agreeNewsletter) {
                useNewsletterSubscribe.mutate({
                  email: myInfoForm.data.email || data.info.email,
                })
              }

              props.onSuccess({
                url: confirmation.handled && confirmation.returnUrl ? confirmation.returnUrl : redirect,
                id: 0,
              })

              // Remember their email for HubSpot
              window._hsq?.push([
                "identify",
                {
                  email: data.info.email,
                },
              ])
            }}
            loading={checkout.isPending}
            data={{
              paymentMethod: {} as any,
              info: myInfoForm.data,
            }}
            render={(form, F) => (
              <div>
                <F.Heading>Billing Details</F.Heading>
                {myInfoForm.render(form.info)}
                <F.Spacer />
                {requiresPayment && (
                  <>
                    <F.Heading>Payment Method</F.Heading>
                    {form.paymentMethod}
                    <F.Spacer />
                  </>
                )}
                <F.Stack>
                  {props.agreements.membership && <F.Describe note={props.agreements.membership}>{form.agreeMembership}</F.Describe>}
                  {props.agreements.newsletter && <F.Describe note={props.agreements.newsletter}>{form.agreeNewsletter}</F.Describe>}
                  {props.agreements.terms && <F.Describe note={props.agreements.terms}>{form.agreeTerms}</F.Describe>}
                </F.Stack>
                <F.SubmitBar submitLabel={requiresPayment ? "Process Payment" : "Continue"}>
                  {props.onGoBack ? (
                    <Button variant="outline" type="button" onClick={props.onGoBack} leftIcon={<IconCaretLeft />}>
                      Back
                    </Button>
                  ) : null}
                </F.SubmitBar>
              </div>
            )}
          />
        </StripePaymentContext>
      </div>
    </div>
  )
}
